import React, { useEffect, useState } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

interface BookingInfo {
  tripDate: string;
  slot: string;
  bookingID: string;
  vehicleInfo: string;
  licensePlate: string;
  pickupAddress: string;
  dropOffAddress: string;
  pickupTime: string;
  dropOffTime: string;
  notes: string;
}

// Create one filled PDF (as bytes) for a single booking
async function createFilledPdf(templateUrl: string, customText: string): Promise<Uint8Array> {
  const existingPdfBytes = await fetch(templateUrl).then(res => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const firstPage = pdfDoc.getPages()[0];
  const { width, height } = firstPage.getSize();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const lines = customText.split('\n');
  const fontSize = 8;
  const lineHeight = fontSize + 2;
  let y = height - 90;

  lines.forEach(line => {
    const sanitized = line.replace(/[^\x20-\x7E]/g, '');
    firstPage.drawText(sanitized, {
      x: 10,
      y,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
    y -= lineHeight;
  });

  return await pdfDoc.save(); // return bytes, not blob
}

// Merge multiple PDFs (given as byte arrays)
async function mergePdfBytes(allBytes: Uint8Array[]) {
  const merged = await PDFDocument.create();
  for (const bytes of allBytes) {
    const src = await PDFDocument.load(bytes);
    const pages = await merged.copyPages(src, src.getPageIndices());
    pages.forEach(page => merged.addPage(page));
  }
  return await merged.save();
}

function BookingChecklist({ bookings }: { bookings: BookingInfo[] }) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    async function buildMerged() {
      const pdfByteArray: Uint8Array[] = [];

      for (const booking of bookings) {
        const templateUrl =
          booking.bookingID.includes('KRE') || booking.bookingID.includes('IKA')
            ? '/MOLL-Übergabeprotokoll.pdf'
            : booking.bookingID.includes('ONL')
            ? '/ONLOGIST-Übergabeprotokoll.pdf'
            : booking.bookingID.includes('MYH')
            ? '/MYHAMMER-Übergabeprotokoll.pdf'
            : booking.bookingID.includes('XTX') || booking.bookingID.includes('FEX')
            ? '/EXTERNAL-Übergabeprotokoll.pdf'
            : null;

        if (!templateUrl) continue;

        const text = `
              Datum: ${booking.tripDate}
              Uhrzeit:
              Tour-Nummer/Auftragsnummer: ${booking.bookingID}
              Marke & Modell: ${booking.vehicleInfo}
              Kennzeichen: ${booking.licensePlate}
              von: ${booking.pickupAddress}
              nach: ${booking.dropOffAddress}
              Geplante Uhrzeit Abholung/Abgabe: ${booking.pickupTime} / ${booking.dropOffTime}
              Hinweise zur Abholung/Abgabe: ${booking.notes}
              `;

        const filledBytes = await createFilledPdf(templateUrl, text);
        pdfByteArray.push(filledBytes);
      }

      if (pdfByteArray.length > 0) {
        const mergedBytes = await mergePdfBytes(pdfByteArray);
        const blob = new Blob([mergedBytes], { type: 'application/pdf' });
        setPdfUrl(URL.createObjectURL(blob));
      }
    }

    buildMerged();
  }, [bookings]);

  return pdfUrl ? (
    <iframe src={pdfUrl} width="100%" height="800px" title="Merged PDF" />
  ) : (
    <p>Loading...</p>
  );
}

export default BookingChecklist;
